import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import PrivateRoutes from "./components/privateRoute/privateRoute";
import { DEVICE_TYPES, PAGES_URL } from "./shared/constants/constants";
import { PLATFORM_PERMISSIONS } from "./shared/models/abacConfig";
import PermissionsGate from "./components/privateRoute/permissionsGate";
import SettingPage from "./components/settingPage/settingPage";
import customTheme from "./shared/models/theme";
import "./App.scss";
import SessionExpirationTimer from "./shared/components/sessionExpireTimer/sessionExpireTimer";

import ApprovalPage from "./components/approvalPage/approvalPage";
import ThreeWayMatch from "./pages/ThreeWayMatch/threeWayMatch";
import RateContract from "./pages/Contract/RateContract/rateContract";
import ListContractRate from "./pages/Contract/RateContract/RateContractList/rateContractList";
import ViewRateContract from "./pages/Contract/RateContract/ViewRateContract/viewRateContract";
import Home from "./pages/Home/home";
import useDisableInspect from "./shared/hooks/useDisableInspect";
import PrivacyPolicy from "./pages/Home/privacyPolicy";
import TermsOfUse from "./pages/Home/termsOfUse";
import OrderInvoiceWizard from "./components/accountPayable/orderInvoiceWizard";
import { getEnvName } from "./shared/utils/utils";
import { store } from "./store/store";
import CustomWindowSize from "./shared/hooks/useWindowSize";
import { setBrowserDeviceType } from "./store/reducers/browserConfig";
const envName = getEnvName();

const OAuthCallback = lazy(
  () => import("./pages/Authentication/OAuthCallback/oauthCallback")
);
const GoogleOAuthRegister = lazy(
  () => import("./pages/Authentication/GAuthReg/googleOAuthRegister")
);
const CreateOrderMatrix = lazy(
  () => import("./pages/Order/CreateOrder/createOrderMatrix")
);
const ProfilePicture = lazy(() => import("./pages/Profile/profilePicture"));
const TrackingPage = lazy(() => import("./pages/Order/Tracking/trackingPage"));
const ViewOrderGRN = lazy(
  () => import("./pages/GRN/ViewAllOrderGRN/ViewOrderGRN")
);
const ViewGRN = lazy(() => import("./pages/GRN/ViewGRN/ViewGRN"));
const HelpPage = lazy(() => import("./pages/Help/helpPage"));
const PushNotification = lazy(
  () => import("./shared/components/pushNotificationHandler/PushNotification")
);
const CloneRFQ = lazy(() => import("./pages/Request/CloneRFQ/cloneRFQ"));
const Reporting = lazy(() => import("./pages/Reporting/Reporting"));
const ChooseRO = lazy(() => import("./pages/RepeatOrder/ChooseRO/chooseRO"));
const CreateRO = lazy(() => import("./pages/RepeatOrder/CreateRO/createRO"));
const ViewRO = lazy(() => import("./pages/RepeatOrder/ViewRO/viewRO"));
const Sellers = lazy(() => import("./pages/Sellers/sellers"));
const UpcomingPage = lazy(() => import("./pages/Upcoming/upcoming"));
const ListRO = lazy(() => import("./pages/RepeatOrder/ListRO/listRO"));
const Bids = lazy(() => import("./pages/Dashboard/bids"));
const Orders = lazy(() => import("./pages/Dashboard/orders"));
const InvoiceList = lazy(
  () => import("./pages/Invoice/InvoiceList/invoiceList")
);
const RFQOrderList = lazy(() => import("./pages/Order/ViewOrder/rfqOrderList"));
const OrderDetails = lazy(
  () => import("./pages/Order/OrderDetails/orderDetails")
);
const RFQHistory = lazy(
  () => import("./pages/Request/ViewRFQHistory/rfqhistory")
);
const OrderHistory = lazy(
  () => import("./pages/Order/ViewOrderHistory/orderHistory")
);
const ListRFQs = lazy(() => import("./pages/Request/ListRFQs/listRFQs"));
const RFQDetails = lazy(() => import("./pages/Request/ViewRFQ/rfqDetails"));

const Authentication = lazy(
  () => import("./pages/Authentication/authentication")
);
const DashboardBody = lazy(() => import("./components/dashboard/dashboardStruct/dashboard"));
const CreateRFQ = lazy(() => import("./pages/Request/CreateRFQ/createRFQ"));
const CreateOrder = lazy(() => import("./pages/Order/CreateOrder/createOrder"));
const CreateInvoice = lazy(
  () => import("./pages/Invoice/CreateInvoice/createInvoice")
);
const ComparisonMatrix = lazy(
  () => import("./pages/Request/ComparisonMatrix/comparisonMatrix")
);
const CreateQuote = lazy(
  () => import("./pages/Quotations/CreateQuote/createQuote")
);
const ViewQuote = lazy(() => import("./pages/Quotations/ViewQuote/viewQuote"));
const UpdateRFQ = lazy(() => import("./pages/Request/UpdateRFQ/updateRFQ"));
const ViewOrder = lazy(() => import("./pages/Order/ViewOrder/viewOrder"));
const GenerateOrderPDF = lazy(
  () => import("./pages/Order/ViewOrder/orderPDFGenerator")
);
const QuoteList = lazy(
  () => import("./pages/Quotations/ListQuotes/listQuotes")
);
const OrderList = lazy(() => import("./pages/Order/ListOrders/listOrders"));
const ApprovalOverview = lazy(
  () => import("./pages/Request/ApprovalOverview/approvalOverview")
);
const ListGRN = lazy(() => import("./pages/GRN/ListGRN/ListGRN"));
const CreateGRN = lazy(() => import("./pages/GRN/CreateGRN/CreateGRN"));
const EditGRN = lazy(() => import("./pages/GRN/EditGRN/EditGRN"));

const InventoryList = lazy(
  () => import("./pages/Inventory/InventoryList/InventoryList")
);
const InventoryIssueStock = lazy(
  () => import("./pages/Inventory/InventoryIssueStock/InventoryIssueStock")
);
const InventoryItemDetails = lazy(
  () => import("./pages/Inventory/InventoryItemDetails/InventoryItemDetails")
);
const InventoryIssueRequestList = lazy(
  () =>
    import(
      "./pages/Inventory/InventoryIssueRequestList/InventoryIssueRequestList"
    )
);
const InventoryApproveStock = lazy(
  () => import("./pages/Inventory/InventoryApproveStock/InventoryApproveStock")
);

const InventoryMerge = lazy(
  () => import("./pages/Inventory/InventoryMerge/inventoryMerge")
);

const DiscoveryAgent = lazy(
  () => import("./pages/DiscoveryAgent/discoveryAgent")
);

const ViewInvoice = lazy(
  () => import("./pages/Invoice/InvoiceView/ViewInvoice")
);
const InvoicePushERP = lazy(
  () => import("./pages/Invoice/InvoicePushERP/InvoicePushERP")
);

const ProformaInvoiceList = lazy(
  () => import("./pages/ProformaInvoice/ProformaInvoiceList/proformaInvoiceList")
);
const ProformaInvoiceView = lazy(
  () => import("./pages/ProformaInvoice/ProformaInvoiceVIew/ViewProformaInvoice")
);

const theme = customTheme;

// <PermissionedRoute path='MyRFQs/CreateRFQ' element={<CreateRFQ />} permissions={[PLATFORM_PERMISSIONS.REQUEST_CREATE]} />

function App() {
  const customWindowSize = CustomWindowSize();

useEffect(()=>{
  if (customWindowSize.width <= 480) {
    store.dispatch(setBrowserDeviceType(DEVICE_TYPES.MOBILE));
  } else if (customWindowSize.width > 1024) {
    store.dispatch(setBrowserDeviceType(DEVICE_TYPES.DESKTOP));
  } else {
    store.dispatch(setBrowserDeviceType(DEVICE_TYPES.TABLET));
  }
}, [customWindowSize.width])
  // useDisableInspect();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <BrowserRouter>
          <Suspense>
            <Routes>
              <Route element={<PrivateRoutes />}>
                <Route path={PAGES_URL.DASHBOARD}>
                  <Route index element={<DashboardBody />} />
                  <Route path="MyRFQs" element={<ListRFQs />} />
                  <Route path="ApprovalFlow" element={<ApprovalPage />} />
                  <Route path={PAGES_URL.SETTINGS} element={<SettingPage />} />
                  <Route
                    path={PAGES_URL.PROFILE}
                    element={<ProfilePicture />}
                  />
                  <Route path={PAGES_URL.HELP} element={<HelpPage />} />
                  <Route
                    path={PAGES_URL.SELLER_ONBOARDING}
                    element={<Sellers />}
                  />
                  <Route path={PAGES_URL.UPCOMING} element={<UpcomingPage />} />

                  {/* <RoleBasedPermissionsGate 
                    roles={[PLATFORM_ROLES.ADMIN, PLATFORM_ROLES.SUPER_ADMIN, PLATFORM_ROLES.SOCIETY_ADMIN]} 
                    isRouteElement={true}
                  > */}
                  <Route path={PAGES_URL.REPORTING} element={<Reporting />} />

                  {/* </RoleBasedPermissionsGate> */}
                </Route>

                <Route path={PAGES_URL.DISCOVERY_AGENT}>
                  <Route index element={<DiscoveryAgent />} />
                </Route>

                <Route path={PAGES_URL.ORDERMGR} >
                  <Route
                    path={PAGES_URL.BIDS}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.REQUEST_VIEW,
                          PLATFORM_PERMISSIONS.REPEAT_ORDER_VIEW,
                        ]}
                        isRouteElement={true}
                      >
                        <Bids />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={PAGES_URL.ORDERS}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.ORDER_VIEW,
                          PLATFORM_PERMISSIONS.GRN_APPROVAL,
                        ]}
                        isRouteElement={true}
                      >
                        <Orders />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={PAGES_URL.CREATE_ORDER}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_CREATE]}
                        isRouteElement={true}
                      >
                        <CreateOrder />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={PAGES_URL.CREATE_RFQ}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REQUEST_CREATE]}
                        isRouteElement={true}
                      >
                        <CreateRFQ />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={PAGES_URL.CLONE_RFQ}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REQUEST_CREATE]}
                        isRouteElement={true}
                      >
                        <CloneRFQ />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={PAGES_URL.RFQ_LIST}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REQUEST_VIEW]}
                        isRouteElement={true}
                      >
                        <ListRFQs />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.VIEW_RFQ}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REQUEST_VIEW]}
                        isRouteElement={true}
                      >
                        <RFQDetails />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.RFQ_HISTORY}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REQUEST_VIEW]}
                        isRouteElement={true}
                      >
                        <RFQHistory />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.UPDATE_RFQ}`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REQUEST_UPDATE]}
                        isRouteElement={true}
                      >
                        <UpdateRFQ />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.VIEW_ORDER}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <RFQOrderList />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.ORDER_DETAILS}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <OrderDetails />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.TRACKING}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <TrackingPage />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.ORDER_HISTORY}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REQUEST_VIEW]}
                        isRouteElement={true}
                      >
                        <OrderHistory />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={PAGES_URL.ORDER_LIST}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <OrderList />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.GRN_LIST}`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <ListGRN />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.GRN_LIST}`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <ListGRN />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.GRN_VIEW}`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.GRN_APPROVAL]}
                        isRouteElement={true}
                      >
                        <ViewGRN />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.CREATE_GRN}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <CreateGRN />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.EDIT_GRN}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <EditGRN />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.GENERATE_ORDER_PDF}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <GenerateOrderPDF />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.CREATE_COMPARISON_MATRIX}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REQUEST_VIEW]}
                        isRouteElement={true}
                      >
                        <ComparisonMatrix />
                        {/* <CreateOrderMatrix/> */}
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.APPROVAL_OVERVIEW}`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REQUEST_VIEW]}
                        isRouteElement={true}
                      >
                        <ApprovalOverview />
                        {/* <CreateOrderMatrix/> */}
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.CREATE_PO_MATRIX}`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REQUEST_VIEW]}
                        isRouteElement={true}
                      >
                        <CreateOrderMatrix />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.VIEW_ALL_ORDER_GRN}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <ViewOrderGRN />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.VIEW_GRN}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <ViewGRN />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.THREE_WAY_MATCH}`}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.ORDER_THREE_WAY_MATCH_UPDATE,
                        ]}
                        isRouteElement={true}
                      >
                        <ThreeWayMatch />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.INVOICE_CREATE}`}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.INVOICE_CREATE,
                          PLATFORM_PERMISSIONS.ORDER_INVOICE_UPDATE,
                          PLATFORM_PERMISSIONS.INVOICE_UPDATE
                        ]}
                        isRouteElement={true}
                      >
                        <OrderInvoiceWizard />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={PAGES_URL.CHOOSE_RO}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.REPEAT_ORDER_CREATE]}
                        isRouteElement={true}
                      >
                        <ChooseRO />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={PAGES_URL.CREATE_RO}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.REPEAT_ORDER_CREATE,
                          PLATFORM_PERMISSIONS.REPEAT_ORDER_UPDATE,
                          PLATFORM_PERMISSIONS.REPEAT_ORDER_VIEW,
                        ]}
                        isRouteElement={true}
                      >
                        <CreateRO />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.VIEW_RO}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.REPEAT_ORDER_VIEW,
                          PLATFORM_PERMISSIONS.ORDER_VIEW,
                        ]}
                        isRouteElement={true}
                      >
                        <ViewRO />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={`${PAGES_URL.RO_LIST}`}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.REPEAT_ORDER_VIEW,
                          PLATFORM_PERMISSIONS.ORDER_VIEW,
                        ]}
                        isRouteElement={true}
                      >
                        <ListRO />
                      </PermissionsGate>
                    }
                  />

                  {envName === "dev" && (
                  <> 
                  <Route
                    path={PAGES_URL.RATE_CONTRACT_CREATE}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_CREATE]}
                        isRouteElement={true}
                      >
                        <RateContract />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={PAGES_URL.RATE_CONTRACT_LIST}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <ListContractRate />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.RATE_CONTRACT_VIEW}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.ORDER_VIEW]}
                        isRouteElement={true}
                      >
                        <ViewRateContract />
                      </PermissionsGate>
                    }
                  />
                  </>
                  )}


                  <Route
                    path={`${PAGES_URL.INVENTORY}`}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.FACILITY_ADMIN,
                          PLATFORM_PERMISSIONS.FACILITY_CREATE,
                          PLATFORM_PERMISSIONS.FACILITY_DELETE,
                          PLATFORM_PERMISSIONS.FACILITY_UPDATE,
                          PLATFORM_PERMISSIONS.FACILITY_VIEW,
                        ]}
                      >
                        <InventoryList />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.INVENTORY_MERGE}`}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.FACILITY_ADMIN,
                          PLATFORM_PERMISSIONS.FACILITY_CREATE,
                          PLATFORM_PERMISSIONS.FACILITY_DELETE,
                          PLATFORM_PERMISSIONS.FACILITY_UPDATE,
                          PLATFORM_PERMISSIONS.FACILITY_VIEW,
                        ]}
                      >
                        <InventoryMerge />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.INVENTORY_ITEM_DETAILS}/:inventoryItemId`}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.FACILITY_ADMIN,
                          PLATFORM_PERMISSIONS.FACILITY_CREATE,
                          PLATFORM_PERMISSIONS.FACILITY_DELETE,
                          PLATFORM_PERMISSIONS.FACILITY_UPDATE,
                          PLATFORM_PERMISSIONS.FACILITY_VIEW,
                        ]}
                      >
                        <InventoryItemDetails />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.INVENTORY_ISSUANCE_REQUEST_CREATE}`}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.FACILITY_ADMIN,
                          PLATFORM_PERMISSIONS.FACILITY_CREATE,
                        ]}
                      >
                        <InventoryIssueStock />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.INVENTORY_ISSUANCE_REQUEST_LIST}`}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.FACILITY_ADMIN,
                          PLATFORM_PERMISSIONS.FACILITY_VIEW,
                          PLATFORM_PERMISSIONS.FACILITY_CREATE,
                          PLATFORM_PERMISSIONS.FACILITY_DELETE,
                          PLATFORM_PERMISSIONS.FACILITY_UPDATE,
                        ]}
                      >
                        <InventoryIssueRequestList />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.INVENTORY_ISSUANCE_REQUEST_VIEW}/:issuanceRequestId`}
                    element={
                      <PermissionsGate
                        permissions={[
                          PLATFORM_PERMISSIONS.FACILITY_ADMIN,
                          PLATFORM_PERMISSIONS.FACILITY_UPDATE,
                        ]}
                      >
                        <InventoryApproveStock />
                      </PermissionsGate>
                    }
                  />
                </Route>
                <Route path={PAGES_URL.QUOTEMGR} >
                  <Route
                    path={PAGES_URL.CREATE_QUOTE}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.QUOTE_CREATE]}
                        isRouteElement={true}
                      >
                        <CreateQuote />
                      </PermissionsGate>
                    }
                  />
                  <Route
                    path={PAGES_URL.UPDATE_QUOTE}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.QUOTE_UPDATE]}
                        isRouteElement={true}
                      >
                        <CreateQuote />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={`${PAGES_URL.VIEW_QUOTE}/:id`}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.QUOTE_VIEW]}
                        isRouteElement={true}
                      >
                        <ViewQuote />
                      </PermissionsGate>
                    }
                  />

                  <Route
                    path={PAGES_URL.QUOTES_LIST}
                    element={
                      <PermissionsGate
                        permissions={[PLATFORM_PERMISSIONS.QUOTE_VIEW]}
                        isRouteElement={true}
                      >
                        <QuoteList />
                      </PermissionsGate>
                    }
                  />
                </Route>

                {/* ================================================== 
                      INVOICE PAGE ROUTES
                      ==================================================*/}

                <Route
                  path={`/${PAGES_URL.ACCOUNT_MGR}`}
                  
                >
                  <Route
                    path={PAGES_URL.INVOICE + "/:invoiceId"}
                    element={<ViewInvoice />}
                  />
                  <Route path={PAGES_URL.INVOICE} element={<InvoiceList />} />

                  <Route
                    path={PAGES_URL.INVOICE_CREATE}
                    element={<CreateInvoice />}
                  />

                  <Route
                    path={PAGES_URL.INVOICE_PUSH_ERP + "/:invoiceId"}
                    element={<InvoicePushERP />}
                  />

                  <Route
                    path={PAGES_URL.PROFORMA_INVOICE + "/:proformaInvoiceId"}
                    element={<ProformaInvoiceView />}
                  />
                  <Route
                    path={PAGES_URL.PROFORMA_INVOICE}
                    element={<ProformaInvoiceList />}
                  />
                </Route>

              </Route>

              <Route path="/forgotPassword" element={<Authentication />} />
              <Route path="/signin" element={<Authentication />} />
              <Route path="/signup" element={<Authentication />} />
              <Route path="/signup-email" element={<Authentication />} />
              <Route path="/sendOtp" element={<Authentication />} />
              <Route path="/verifyOtp" element={<Authentication />} />
              <Route path="/gstDetails" element={<Authentication />} />
              <Route path="/" element={<Authentication />} />
              {/* <Route path="/" element={<Home />} /> */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />

              <Route path="/auth/callback" element={<OAuthCallback />} />
              {/* <Route path="/register" element={<Authentication />} /> */}
              <Route path="*" element={<Authentication />} />
            </Routes>
            <SessionExpirationTimer />
            <PushNotification />
          </Suspense>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
